// Academy 主页: 介绍 + 4 阶梯 + 9 分支入口 + 知识树链接
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  CURRICULUM, BRANCH_ORDER, LEVEL_ORDER, getCurriculumStats, getCellRepresentative
} from '../lib/curriculum.js';
import { useI18n, t } from '../lib/i18n.js';
import { useDocumentTitle } from '../hooks/useDocumentTitle.js';

const LEVEL_INFO = {
  1: { name_zh: '火花', name_en: 'Spark',    icon: '🔥', color: 'from-rose-500 to-pink-500',     abilities_zh: '提问 · 倾听 · 分类',          abilities_en: 'Ask · Listen · Classify' },
  2: { name_zh: '探究', name_en: 'Explorer', icon: '🔍', color: 'from-amber-500 to-orange-500',   abilities_zh: '调研 · AI 协作 · 画图',       abilities_en: 'Research · AI · Visualize' },
  3: { name_zh: '实践', name_en: 'Solver',   icon: '⚙️', color: 'from-emerald-500 to-teal-500',   abilities_zh: '建模 · 实验 · 表达',          abilities_en: 'Model · Experiment · Present' },
  4: { name_zh: '创造', name_en: 'Creator',  icon: '🌟', color: 'from-violet-500 to-fuchsia-500', abilities_zh: '原创 · 领导 · 跨学科',         abilities_en: 'Originate · Lead · Cross-discipline' },
};

export default function Academy() {
  const { lang } = useI18n();
  useDocumentTitle('🎓 ' + (lang === 'zh-CN' ? '硬问题学院' : 'HardProblems Academy'));
  const stats = getCurriculumStats();
  const isZh = lang === 'zh-CN';

  return (
    <div className="container-page py-8 max-w-7xl mx-auto">
      {/* Hero */}
      <section className="card bg-gradient-to-br from-violet-500/10 via-fuchsia-500/5 to-cyan-500/10 border-violet-500/20 mb-8">
        <div className="text-center py-8 px-4">
          <div className="text-6xl mb-3">🎓</div>
          <h1 className="text-4xl sm:text-5xl font-display font-extrabold gradient-text mb-4">
            {isZh ? '硬问题学院' : 'HardProblems Academy'}
          </h1>
          <p className="text-lg text-slate-300 max-w-3xl mx-auto leading-relaxed mb-4">
            {isZh
              ? '让任何人都能用 AI 解决世界上最难的问题。9 大学科、4 阶梯能力成长、5 人小组协作、原创贡献。'
              : 'Let anyone solve the world\'s hardest problems with AI. 9 disciplines, 4 levels of mastery, 5-person teams, original contributions.'}
          </p>
          <div className="flex flex-wrap justify-center gap-3 mt-4">
            <Link to="/academy/tree" className="px-5 py-2.5 rounded-lg bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white font-medium hover:scale-105 transition-transform">
              {isZh ? '🌳 查看知识树' : '🌳 Knowledge Tree'}
            </Link>
            <Link to="/academy/me" className="px-5 py-2.5 rounded-lg bg-white/10 border border-white/20 text-slate-100 font-medium hover:bg-white/15">
              {isZh ? '👤 我的进度' : '👤 My Progress'}
            </Link>
          </div>
          <div className="mt-6 flex flex-wrap justify-center gap-6 text-sm text-slate-400">
            <span>📚 <b className="text-slate-100">{stats.total}</b> {isZh ? '题' : 'problems'}</span>
            <span>🌲 <b className="text-slate-100">9</b> {isZh ? '学科' : 'disciplines'}</span>
            <span>📊 <b className="text-slate-100">36</b> {isZh ? '知识 cell' : 'knowledge cells'}</span>
            <span>🌉 <b className="text-slate-100">10</b> {isZh ? '跨学科桥' : 'cross-bridges'}</span>
          </div>
        </div>
      </section>

      {/* 4 阶梯 */}
      <section className="mb-10">
        <h2 className="text-2xl font-display font-bold mb-4">
          {isZh ? '🪜 4 阶梯 (按能力,不按年龄)' : '🪜 4 Levels (by mastery, not age)'}
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {LEVEL_ORDER.map(lv => {
            const info = LEVEL_INFO[lv];
            return (
              <div key={lv} className={`card bg-gradient-to-br ${info.color}/10 border-white/10`}>
                <div className="flex items-center gap-3 mb-3">
                  <div className="text-4xl">{info.icon}</div>
                  <div>
                    <div className="text-xs text-slate-500 font-mono">L{lv}</div>
                    <div className="text-xl font-bold">{isZh ? info.name_zh : info.name_en}</div>
                  </div>
                </div>
                <p className="text-sm text-slate-300 leading-relaxed">
                  {isZh ? info.abilities_zh : info.abilities_en}
                </p>
              </div>
            );
          })}
        </div>
      </section>

      {/* 9 大分支 */}
      <section className="mb-10">
        <h2 className="text-2xl font-display font-bold mb-2">
          {isZh ? '🌿 9 大学科' : '🌿 9 Disciplines'}
        </h2>
        <p className="text-sm text-slate-400 mb-4">
          {isZh
            ? '每个学科下有 4 阶梯 (L1-L4)。点开看具体题。'
            : 'Each discipline has 4 levels (L1-L4). Click to explore.'}
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {BRANCH_ORDER.map(branchId => {
            const b = CURRICULUM.branches[branchId];
            const branchStats = stats.by_branch[branchId] || 0;
            return (
              <Link
                key={branchId}
                to={`/academy/branch/${branchId.toLowerCase()}`}
                className="card hover:scale-[1.02] transition-transform bg-gradient-to-br from-white/5 to-white/0 hover:border-violet-500/40"
              >
                <div className={`flex items-start gap-3`}>
                  <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${b.color} flex items-center justify-center text-2xl flex-shrink-0`}>
                    {b.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <h3 className="text-lg font-bold">{isZh ? b.name_zh : b.name_en}</h3>
                      <span className="text-xs font-mono px-2 py-0.5 rounded bg-white/5 text-slate-400">{branchStats}</span>
                    </div>
                    <p className="text-xs text-slate-400 leading-relaxed line-clamp-2">
                      {isZh ? b.desc_zh : b.desc_en}
                    </p>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* 教学法 */}
      <section className="card bg-gradient-to-br from-emerald-500/5 to-cyan-500/5 border-emerald-500/20">
        <h2 className="text-2xl font-display font-bold mb-4">
          {isZh ? '💡 教学法 (受 Musk Ad Astra + Khanmigo 启发)' : '💡 Pedagogy (inspired by Musk Ad Astra + Khanmigo)'}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <div className="flex gap-3">
              <div className="text-2xl">🎯</div>
              <div>
                <div className="font-medium">{isZh ? '教问题不教工具' : 'Teach problems, not tools'}</div>
                <p className="text-sm text-slate-400">
                  {isZh ? '从真实问题出发,孩子自然地需要数学/物理/编程/伦理。' : 'Start with a real problem; kids naturally pull in math/physics/ethics.'}
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <div className="text-2xl">🤖</div>
              <div>
                <div className="font-medium">{isZh ? 'AI 是苏格拉底式朋友' : 'AI as a Socratic friend'}</div>
                <p className="text-sm text-slate-400">
                  {isZh ? 'AI 朋友不写答案,只提问和挑战。' : 'AI friend asks questions, never gives answers.'}
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <div className="text-2xl">👥</div>
              <div>
                <div className="font-medium">{isZh ? '5 人小组 + 5 种角色' : '5-person teams + 5 roles'}</div>
                <p className="text-sm text-slate-400">
                  {isZh ? '研究员/实验员/数据员/表达员/协调员,每 2 周轮换。' : 'Researcher/Maker/Analyst/Storyteller/Facilitator, rotate every 2 weeks.'}
                </p>
              </div>
            </div>
          </div>
          <div className="space-y-3">
            <div className="flex gap-3">
              <div className="text-2xl">⭐</div>
              <div>
                <div className="font-medium">{isZh ? 'Karma 而非排名' : 'Karma, not rank'}</div>
                <p className="text-sm text-slate-400">
                  {isZh ? '你贡献,你获得;不是按考试排名。' : 'You contribute, you earn; not ranked by exams.'}
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <div className="text-2xl">🧒</div>
              <div>
                <div className="font-medium">{isZh ? '不分年级' : 'No grade levels'}</div>
                <p className="text-sm text-slate-400">
                  {isZh ? '一个 11 岁的孩子能与 14 岁的孩子在同一 cell 合作。' : 'An 11-year-old can work alongside a 14-year-old on the same cell.'}
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <div className="text-2xl">🌍</div>
              <div>
                <div className="font-medium">{isZh ? '跨学科桥' : 'Cross-discipline bridges'}</div>
                <p className="text-sm text-slate-400">
                  {isZh ? '10 座桥把 9 大学科连起来 (弦论 ↔ 量子引力、酶 ↔ 蛋白折叠…)' : '10 bridges connect 9 disciplines (string theory ↔ quantum gravity, enzymes ↔ folding…)'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
